/**
 * @module ReduxStore
 *
 * @desc
 * ### Definition
 *
 * This module contains redux store creation piece. This is where all the static reducers are connected.
 * We also configure the store middlewares and ability to inject custom reduceres here. See methods for more information.
 *
 * ### Adding dynamic reducers from other apps.
 * - [makeReduxStore]{@link module:ReduxStore#makeReduxStore} initiates the controller for adding and managing dynamic reducers from other apps.
 * It uses [`Reducer Manager`]{@link module:ReducerManager} to add the ability to inject reducers after the initial store creation.
 * - `add` hook from Reducer Manager is attached to the store object at `store.injectReducer`
 * - This is useful to inject reducers for custom apps, like games or ecomm integrations to maintain a their data in state and make it available to other components.
 * - To inject a custom reducer from any app see the following snippet.
 * ```
 * // Get store using the `useStore` hook from react-redux
 * import {useStore } from 'react-redux';
 * const store = useStore();

 * // Injecting custom reducers
 *
 * // Define the reducer
 * const myCustomReducer = {...handlers}
 *
 *
 * // Injection
 * store.injectReducer('myCustomSlice', myCustomReducer)
 *
 * ...
 *
 * // The data from your reducer will be then available at state.myCustomSlice.
 * // To select data
 * const data = useSelector(state => state.myCustomSlice.keyToSelect')
 *
 *
 * ```
 *
 */

import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';

/**  Reducers * */
import scenesReducer from './scenesReducer';
import modalReducer from './modalsReducer';
import componentConfigReducer from './componentConfigReducer';
import storeDataReducer from './storeDataReducer';
import functionsReducer from './functionsReducer';
import analyticsReducer from './analyticsReducer';
import accessibilityReducer from './accessibilityReducer';
import defaultIconsReducer from './defaultIconsReducer';
import sceneLoadReducer from './sceneLoadReducer';
import localizeReducer from './localizeReducer';
import roomObjectReducer from './roomObjectsReducer';
import mediaControllerReducer from './mediaControllerReducer';
import creatorTools from './creatorTools';
import storeAvatarsReducer from './storeAvatarsReducer';
import soundHotspotReducer from '@wsc/redux_stores/soundHotspotReducer';
import xrReducer from './xrReducer';
import { createReducerManager } from '@wsc/redux_stores/reducerManager';

/**
 * @member staticReducers
 * @type Object
 * @desc All the default reducers are attached to the store from here.
 *
 * @property scenes: Scenes reducer, @see [Scenes Reducer]{@link module:ScenesReducer} for more details
 * @property storeData: Store data actions reducer,
 *
 */
const staticReducers = {
	scenes: scenesReducer,
	storeData: storeDataReducer,
	modalData: modalReducer,
	componentConfig: componentConfigReducer,
	shareableFunctions: functionsReducer,
	analytics: analyticsReducer,
	accessibility: accessibilityReducer,
	defaultIcons: defaultIconsReducer,
	sceneLoad: sceneLoadReducer,
	localize: localizeReducer,
	roomObjects: roomObjectReducer,
	mediaController: mediaControllerReducer,
	creatorTools,
	storeAvatars: storeAvatarsReducer,
	soundHotspot: soundHotspotReducer,
	xrReducer: xrReducer,
};

/**
 * @method createEnhancers
 *
 * @desc
 * - Compose enhancers object here, currently we only have thunk middleware
 * - dev tools are disabled on production Node environment here. Disables it for prod and client builds.
 *
 * @return {Object}
 */
const createEnhancers = () => {
	const middlewares = [thunk];
	const middlewareEnhancer = applyMiddleware(...middlewares);
	const enhancers = [middlewareEnhancer];
	return process.env.NODE_ENV === 'development'
		? composeWithDevTools({ trace: true })(...enhancers)
		: compose(...enhancers);
};

/**
 * Creates and return a redux store object
 *
 * - Initiates reducerManager [Reducer Manager]{@link module:ReducerManager}.
 * - Attaches [reducerManager.add]{@link module:ReducerManager#add} to store.injectReducer. This can be used for injecting custom reducers after the initial creation of the store.
 *
 * @return {Object}
 */
const makeReduxStore = () => {
	const enhancers = createEnhancers();
	const reducerManager = createReducerManager(staticReducers);
	const store = createStore(reducerManager.reduce, enhancers);
	store.asyncReducers = {};
	store.injectReducer = reducerManager.add;
	return store;
};

const store = makeReduxStore();
export default store;
